import { AdminMatches } from '@platform/client';
import { MatchGroupData } from '@platform/client/schemas';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo } from 'react';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { MatchGroupLink } from '../matches/MatchesAdmin';
import { TeamLink } from '../teams/TeamsResultsTab';

const columnHelper = createColumnHelper<MatchGroupData>();

export const MatchesTabContent = ({ projectId }: { projectId: number }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const {
    data: matchData,
    isError,
    isLoading,
    isSuccess,
  } = AdminMatches.useGetProjectAdminMatches(projectId, {
    page: adminQueryOptionsV2.page,
    limit: adminQueryOptionsV2.limit,
    sort: adminQueryOptionsV2.sort,
  });
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'Id',
          cell: (info) => (
            <MatchGroupLink
              matchGroupId={info.getValue()}
              matchGroupName={info.getValue().toString()}
            />
          ),
          size: 5,
        }),
        columnHelper.accessor('initiation_type', {
          id: 'initiation_types',
          header: 'Sent by',
          cell: (info) => info.getValue(),
          size: 10,
        }),
        columnHelper.accessor('created_at', {
          header: 'Created',
          cell: (info) => formatDateStr(info.getValue()),
          size: 1,
        }),
        columnHelper.accessor('representative', {
          id: 'representative_name',
          header: 'Representative',
          cell: (info) =>
            info.getValue()?.firstname + ' ' + info.getValue()?.lastname.charAt(0) + '.',
        }),
        columnHelper.accessor('professionals', {
          header: 'Professional',
          cell: ({ row }) => {
            return row.original.team_id && row.original.cohort ?
                <TeamLink
                  teamId={row.original.team_id}
                  cohortId={row.original.cohort.id}
                  text={`Team #${row.original.team_id}`}
                />
              : <ProfessionalLink
                  userId={row.original.professionals[0].id}
                  name={
                    row.original.professionals[0].firstname +
                    ' ' +
                    row.original.professionals[0].lastname.charAt(0) +
                    '.'
                  }
                />;
          },
          enableSorting: false,
        }),
        columnHelper.accessor('progress', {
          id: 'progresses',
          header: 'Progress',
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('updated_at', {
          header: 'Updated',
          cell: (info) => formatDateStr(info.getValue()),
          size: 10,
        }),
      ] as ColumnDef<MatchGroupData>[],
    [],
  );

  return (
    <ProfileTabContainer>
      {isSuccess && matchData?.matches.length === 0 ?
        <NoRecordsFoundTabContent recordType='matches' />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={matchData?.matches || []}
          isSimple
          filteredCount={matchData?.filtered_count ?? 0}
        />
      }
    </ProfileTabContainer>
  );
};
